import React, { useState, useRef, useEffect } from 'react';
import ThemedButton from 'src/components/Forms/components/ThemedButton';
import './styles.scss';

// Client-side only wrapper
const ClientOnlyRichTextEditor = ({ children, ...props }) => {
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  if (!hasMounted) {
    return (
      <div className="rich-text-editor d-flex flex-grow-1 flex-column">
        <div className="editor-container" style={{ height: props.height || '300px' }}>
          <div
            style={{
              padding: '20px',
              textAlign: 'center',
              color: '#6c757d',
              border: '1px solid #ced4da',
              borderRadius: '0.375rem'
            }}
          >
            Loading editor...
          </div>
        </div>
      </div>
    );
  }

  return children;
};

const RichTextEditor = ({
  value,
  onChange,
  placeholder = "Start typing your email content...",
  height = "800px",
  showCopyButton = true,
  copyButtonText = "Copy HTML to Clipboard",
  /** Rendered next to the copy button (e.g. Preview and Send) */
  toolbarExtra = null,
  header = null,
  autoHeight = true,
  containerStyle = {},
}) => {
  const [copied, setCopied] = useState(false);
  const [calculatedHeight, setCalculatedHeight] = useState(height);
  const editorRef = useRef(null);
  const editorContainerRef = useRef(null);
  const isInternalChange = useRef(false);

  // Check if height is a percentage or "100%" - if so, use it directly without calculation
  const isPercentageHeight =
    typeof height === 'string' && (height.includes('%') || height === '100%');

  // Calculate height based on content (only if using autoHeight and not percentage height)
  const calculateHeight = React.useCallback(() => {
    // Skip calculation if using percentage height or autoHeight is disabled
    if (isPercentageHeight || !autoHeight) return;
    
    if (!editorRef.current || !editorContainerRef.current) return;
    
    // Temporarily remove height constraint to get accurate measurement
    const originalHeight = editorContainerRef.current.style.height;
    editorContainerRef.current.style.height = 'auto';
    
    // Measure the actual content height
    const contentHeight = editorRef.current.scrollHeight;
    
    // Restore original height temporarily
    editorContainerRef.current.style.height = originalHeight;
    
    // Set minimum height (e.g., 200px) and maximum height (e.g., 1200px)
    const minHeight = 200;
    const maxHeight = 1200;
    const newHeight = Math.min(Math.max(contentHeight, minHeight), maxHeight);
    
    // Set the new calculated height
    setCalculatedHeight(`${newHeight}px`);
  }, [isPercentageHeight]);

  // Set initial content when editorRef becomes available (using a state to track ref availability)
  const [refReady, setRefReady] = useState(false);
  
  useEffect(() => {
    if (editorRef.current && !refReady) {
      console.log('[RichTextEditor] Ref became ready');
      setRefReady(true);
    }
  }, [refReady]);

  // Update editor content when value prop changes (but not from internal edits)
  useEffect(() => {
    console.log('[RichTextEditor] Value changed', {
      hasEditorRef: !!editorRef.current,
      refReady,
      valueLength: value?.length || 0,
      isInternalChange: isInternalChange.current,
      valuePreview: value?.substring(0, 50)
    });
    
    const setContent = () => {
      if (editorRef.current && value !== undefined && !isInternalChange.current) {
        const currentContent = editorRef.current.innerHTML || '';
        if (currentContent !== value) {
          console.log('[RichTextEditor] Setting innerHTML, length:', value?.length || 0);
          editorRef.current.innerHTML = value || '';
          setTimeout(() => {
            calculateHeight();
          }, 0);
        }
      }
    };
    
    // If editorRef is not ready yet, wait a bit and try again (with multiple retries)
    if (!editorRef.current && value !== undefined && value.length > 0) {
      console.log('[RichTextEditor] EditorRef not ready, scheduling retries');
      let retryCount = 0;
      const maxRetries = 10;
      const retryInterval = 50;
      
      const retryId = setInterval(() => {
        retryCount++;
        if (editorRef.current) {
          console.log('[RichTextEditor] EditorRef ready after', retryCount, 'retries');
          clearInterval(retryId);
          setContent();
        } else if (retryCount >= maxRetries) {
          console.log('[RichTextEditor] Max retries reached, giving up');
          clearInterval(retryId);
        }
      }, retryInterval);
      
      return () => clearInterval(retryId);
    } else if (editorRef.current) {
      setContent();
    }
    
    isInternalChange.current = false;
  }, [value, calculateHeight, refReady]);
  
  // Also set content when ref becomes ready
  useEffect(() => {
    if (refReady && editorRef.current && value !== undefined && !isInternalChange.current) {
      const currentContent = editorRef.current.innerHTML || '';
      if (currentContent !== value) {
        console.log('[RichTextEditor] Setting content when ref becomes ready, length:', value?.length || 0);
        editorRef.current.innerHTML = value || '';
        setTimeout(() => {
          calculateHeight();
        }, 0);
      }
    }
  }, [refReady, value, calculateHeight]);

  // Recalculate height on mount and when content changes (only if using autoHeight and not percentage height)
  useEffect(() => {
    // Skip if using percentage height or autoHeight is disabled
    if (isPercentageHeight || !autoHeight) return;
    
    if (editorRef.current) {
      // Debounce function to avoid excessive recalculations
      let timeoutId;
      const debouncedCalculate = () => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          calculateHeight();
        }, 100);
      };
      
      // Use ResizeObserver to watch for content changes
      const resizeObserver = new ResizeObserver(debouncedCalculate);
      
      resizeObserver.observe(editorRef.current);
      
      // Initial calculation after a brief delay to ensure content is rendered
      setTimeout(() => {
        calculateHeight();
      }, 50);
      
      return () => {
        clearTimeout(timeoutId);
        resizeObserver.disconnect();
      };
    }
  }, [calculateHeight, isPercentageHeight]);

  // Handle content changes
  const handleInput = () => {
    if (editorRef.current && onChange) {
      isInternalChange.current = true;
      onChange(editorRef.current.innerHTML);
      // Recalculate height after content change
      setTimeout(() => {
        calculateHeight();
      }, 0);
    }
  };

  // Formatting commands
  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  };

  // Copy HTML to clipboard
  const handleCopy = async () => {
    if (!editorRef.current) return;

    const htmlContent = `<div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0; ">${editorRef.current.innerHTML}</div>`;
    console.log('htmlContent', "'" + htmlContent)
    try {
      // Use modern Clipboard API if available
      if (navigator.clipboard && window.ClipboardItem) {
        const blob = new Blob([htmlContent], { type: 'text/html' });
        
        const clipboardItem = new ClipboardItem({ 'text/html': blob });

        await navigator.clipboard.write([clipboardItem]);
      } else {
        // Fallback to execCommand for older browsers
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlContent;
        tempDiv.style.position = 'absolute';
        tempDiv.style.left = '-9999px';
        tempDiv.style.top = '-9999px';
        document.body.appendChild(tempDiv);

        const range = document.createRange();
        range.selectNodeContents(tempDiv);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        document.execCommand('copy');
        selection.removeAllRanges();
        document.body.removeChild(tempDiv);
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // Fallback: try copying as plain text
      try {
        await navigator.clipboard.writeText(htmlContent);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (fallbackErr) {
        console.error('Fallback copy also failed:', fallbackErr);
      }
    }
  };

  return (
    <ClientOnlyRichTextEditor height={height}>
      <div className="rich-text-editor d-flex flex-grow-1 flex-column">

        {/* Toolbar */}
        <div className="editor-header">
          {/* Header */}
          {header && (
            <div className="editor-header-content">
              {header}
            </div>
          )}
          <div className="editor-toolbar-content">
            <div className="editor-toolbar-buttons">
              <button
                type="button"
                className="toolbar-btn"
                onClick={() => execCommand('bold')}
                title="Bold"
              >
                <strong>B</strong>
              </button>
              <button
                type="button"
                className="toolbar-btn"
                onClick={() => execCommand('italic')}
                title="Italic"
              >
                <em>I</em>
              </button>
              <button
                type="button"
                className="toolbar-btn"
                onClick={() => execCommand('underline')}
                title="Underline"
              >
                <u>U</u>
              </button>
              <div className="toolbar-separator" />
              <button
                type="button"
                className="toolbar-btn"
                onClick={() => execCommand('formatBlock', '<h1>')}
                title="Heading 1"
              >
                H1
              </button>
              <button
                type="button"
                className="toolbar-btn"
                onClick={() => execCommand('formatBlock', '<h2>')}
                title="Heading 2"
              >
                H2
              </button>
              <button
                type="button"
                className="toolbar-btn"
                onClick={() => execCommand('formatBlock', '<p>')}
                title="Paragraph"
              >
                P
              </button>
              <div className="toolbar-separator" />
              <button
                type="button"
                className="toolbar-btn"
                onClick={() => execCommand('insertUnorderedList')}
                title="Bullet List"
              >
                •
              </button>
              <button
                type="button"
                className="toolbar-btn"
                onClick={() => execCommand('insertOrderedList')}
                title="Numbered List"
              >
                1.
              </button>
              <div className="toolbar-separator" />
              <button
                type="button"
                className="toolbar-btn"
                onClick={() => {
                  const url = prompt('Enter link URL:');
                  if (url) execCommand('createLink', url);
                }}
                title="Insert Link"
              >
                🔗
              </button>
              <button
                type="button"
                className="toolbar-btn"
                onClick={() => {
                  const url = prompt('Enter image URL:');
                  if (url) execCommand('insertImage', url);
                }}
                title="Insert Image"
              >
                🖼️
              </button>
            </div>
            {showCopyButton && (
              <div className="editor-toolbar-copy d-flex align-items-center flex-wrap">
                <ThemedButton
                  type="button"
                  variant="outline-primary"
                  className="btn btn-primary"
                  size="sm"
                  onClick={handleCopy}
                >
                  {copied ? 'Copied!' : copyButtonText}
                </ThemedButton>
                {toolbarExtra ? (
                  <span className="ml-2 d-inline-flex align-items-center">{toolbarExtra}</span>
                ) : null}
                {copied && (
                  <small className="text-success ml-2">✓ HTML content copied to clipboard!</small>
                )}
              </div>
            )}
            {!showCopyButton && toolbarExtra ? (
              <div className="editor-toolbar-copy d-flex align-items-center flex-wrap">
                {toolbarExtra}
              </div>
            ) : null}
          </div>
        </div>

        {/* Editor */}
        <div
          className="editor-container d-flex flex-grow-1"
          ref={editorContainerRef}
          style={{

            ...containerStyle,
          }}
        >
          <div
            ref={editorRef}
            contentEditable
            className="editor-content"
            onInput={handleInput}
            onPaste={(e) => {
              // Allow pasting HTML
              e.preventDefault();
              const text = (e.clipboardData || window.clipboardData).getData('text/html') ||
                          (e.clipboardData || window.clipboardData).getData('text/plain');
              document.execCommand('insertHTML', false, text);
              // Recalculate height after paste
              setTimeout(() => {
                calculateHeight();
              }, 0);
            }}
            data-placeholder={placeholder}
            suppressContentEditableWarning
          />
        </div>
      </div>
    </ClientOnlyRichTextEditor>
  );
};

export default RichTextEditor;
